import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import PostCard from './PostCard';
import CreatePostModal from './CreatePostModal';
import PageHeader from 'app/design/speedBot/PageHeader';
import MuiBreadcrumbs from 'app/design/uiComponents/MuiBreadcrumbs';
import { Box, Button, Divider, Slide, useTheme, Tabs, Tab } from '@mui/material';
import CircularLoader from 'app/design/uiComponents/CircularLoader';
import { Helmet } from 'react-helmet-async';
import MuiChip from 'app/design/uiComponents/MuiChip';
import MuiButton from 'app/design/uiComponents/MuiButton';
import { 
  useGetCommunityIdeas, 
  useGetCommunityStrategies,
  useCreateCommunityIdea,
  useCreateCommunityStrategy
} from 'services/Community';
import { CreateIdeaParams, CreateStrategyParams } from 'types/ApiServicesTypes';
import ContentTabs from 'app/design/itemsComponent/ContentTabs';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

interface CreatePostData {
  title: string;
  images: string[];
  type: 'ideas' | 'strategies';
  content_blocks: {
    id: string;
    type: 'text' | 'image';
    content: string;
    image_url?: string;
    image_alt?: string;
    sequence: number;
  }[];
  tag_ids: number[];
  category_id?: number;
}

// Custom Tab Component without MUI styling
const CustomTabs: React.FC<{
  value: number;
  onChange: (value: number) => void;
  tabs: { label: string }[];
}> = ({ value, onChange, tabs }) => {
  const theme = useTheme();
  
  return (
    <Box sx={{ display: 'flex', gap: 0 }}>
      {tabs.map((tab, index) => (
        <Box
          key={index}
          onClick={() => onChange(index)}
          sx={{
            padding: '8px 16px',
            cursor: 'pointer',
            fontWeight: 500,
            fontSize: '14px',
            transition: theme.transitions.create(['background-color', 'color'], {
              duration: theme.transitions.duration.short,
            }),
            color: value === index ? theme.palette.common.white : theme.palette.text.secondary,
            backgroundColor: value === index ? theme.palette.primary.dark : 'transparent',
            '&:hover': {
              backgroundColor: value === index 
                ? theme.palette.primary.light 
                : theme.palette.action.hover,
            },
          }}
        >
          {tab.label}
        </Box>
      ))}
    </Box>
  );
};

const CommunityPage: React.FC = () => {
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const theme = useTheme();
  const history = useHistory();

  // API Hooks
  const ideasQuery = useGetCommunityIdeas(1, 20);
  const strategiesQuery = useGetCommunityStrategies(1, 20);
  const createIdeaMutation = useCreateCommunityIdea();
  const createStrategyMutation = useCreateCommunityStrategy();

  const handleCreatePost = (newPost: CreatePostData) => {
    const postData: CreateIdeaParams | CreateStrategyParams = {
      title: newPost.title,
      images: newPost.images || [],
      tag_ids: newPost.tag_ids || [],
      category_id: newPost.category_id,
      content_blocks: newPost.content_blocks.map(block => ({
        type: block.type,
        content: block.content,
        image_url: block.image_url,
        image_alt: block.image_alt,
        sequence: block.sequence
      }))
    };

    if (newPost.type === 'ideas') {
      createIdeaMutation.mutate(postData, {
        onSuccess: () => {
          setShowModal(false);
        }
      });
    } else {
      createStrategyMutation.mutate(postData as CreateStrategyParams, {
        onSuccess: () => {
          setShowModal(false);
        }
      });
    }
  };

  const handleTabChange = (event: React.SyntheticEvent | number) => {
    setActiveTab(event as number);
  };

  const handlePostClick = (post: { id: number }) => {
    const postType = activeTab === 0 ? 'ideas' : 'strategies';
    history.push(`/community/${postType}/${post.id}`);
  };

  // Get data from API
  const ideasPosts = ideasQuery.data?.data || [];
  const strategiesPosts = strategiesQuery.data?.data || [];

  // Get button text based on active tab
  const getCreateButtonText = () => {
    return activeTab === 0 ? 'Create Idea' : 'Create Strategy';
  };

  // Get default type for modal based on active tab
  const getDefaultType = () => {
    return activeTab === 0 ? 'ideas' : 'strategies';
  };

  // Check if any query is loading
  const isLoading = ideasQuery.isLoading || strategiesQuery.isLoading;

  return (
    <Box>
      <CircularLoader open={isLoading} zIndex={1302} />
      <Helmet>
        <title>Community </title>
        <meta name="description" content="Community Page" />
      </Helmet>
      <Box className="sticky-top" sx={{
        backgroundColor: theme.palette.common.white,
        transition: theme.transitions.create(['transform', 'opacity'], {
          duration: theme.transitions.duration.standard,
        }),
      }}>

        <PageHeader
          variant="market"
          title={
            <>
              Community
              <MuiChip
                label={'Beta'}
                size="medium"
                color={'greylight'}
                sx={{ m: 1 }}
              />
            </>
          }
        />
        <Divider sx={{ borderBottom: 2, borderColor: 'grey.300' }} />
      </Box>

      {/* Tabs with Create Button inline */}
      <Box sx={{ borderBottom: 4, borderColor: 'divider', px: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {/* <Box sx={{ 
          flex: 1,
          '& .MuiTabs-root': {
            minHeight: 'auto',
            '& .MuiTabs-indicator': {
              display: 'none', // Remove the indicator line
            },
            '& .MuiTabs-flexContainer': {
              gap: 2,
            },
          },
          '& .MuiTab-root': {
            minHeight: 'auto',
            padding: '8px 16px',
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '14px',
            color: theme.palette.text.secondary,
            '&.Mui-selected': {
              color: theme.palette.primary.main,
              backgroundColor: theme.palette.primary.light,
              borderRadius: theme.shape.borderRadius,
            },
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
              borderRadius: theme.shape.borderRadius,
            },
          },
          '&:after': {
            display: 'none', // Remove the bottom border from ContentTabs
          },
        }}>
          <ContentTabs 
            value={activeTab} 
            onChange={handleTabChange} 
            aria-label="community tabs" 
            tabs={[{label: `Ideas (${ideasPosts.length})`}, {label: `Strategies (${strategiesPosts.length})`}]} 
          />
        </Box> */}
        
        {/* Alternative: Custom Tabs without any MUI styling */}
        
        <CustomTabs
          value={activeTab}
          onChange={handleTabChange}
          tabs={[{label: `Ideas (${ideasPosts.length})`}, {label: `Strategies (${strategiesPosts.length})`}]}
        />
       
        <MuiButton
          variant="contained"
          color="primary"
          size="small"
          onClick={() => setShowModal(true)}
          disabled={createIdeaMutation.isLoading || createStrategyMutation.isLoading}
        >
          {getCreateButtonText()}
        </MuiButton>
      </Box>

      {/* Ideas Tab Panel */}
      <TabPanel value={activeTab} index={0}>
        <Box
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
            gap: 24,
            padding: '0 24px',
          }}
        >
          {ideasPosts.map(post => (
            <PostCard key={post.id} post={post} onClick={() => handlePostClick(post)} />
          ))}
          {ideasPosts.length === 0 && !isLoading && (
            <Box sx={{ gridColumn: '1 / -1', textAlign: 'center', py: 4, color: 'text.secondary' }}>
              No ideas found. Be the first to create one!
            </Box>
          )}
        </Box>
      </TabPanel>

      {/* Strategies Tab Panel */}
      <TabPanel value={activeTab} index={1}>
        <Box
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
            gap: 24,
            padding: '0 24px',
          }}
        >
          {strategiesPosts.map(post => (
            <PostCard key={post.id} post={post} onClick={() => handlePostClick(post)} />
          ))}
          {strategiesPosts.length === 0 && !isLoading && (
            <Box sx={{ gridColumn: '1 / -1', textAlign: 'center', py: 4, color: 'text.secondary' }}>
              No strategies found. Be the first to create one!
            </Box>
          )}
        </Box>
      </TabPanel>

      {showModal && (
        <CreatePostModal 
          onClose={() => setShowModal(false)} 
          onCreate={handleCreatePost}
          defaultType={getDefaultType()}
        />
      )}
    </Box>
  );
};

export default CommunityPage;
